import type { Body, BodyUUID, Material, MaterialUUID, WorldBody } from "../../types/scene";
import { create } from "mutative";
import { logger } from "../../utils/logger";
import { shouldBlockPhysicsMutation } from "../../utils/physicsGuards";
import type { SceneStoreSetState } from "../sceneStore.types";
import type { BodyActions } from "./actionTypes";
import { cleanupReferencesForDeletedTargets } from "./referenceCleanup";
import {
  flattenBodyTree,
  generateUniqueWorldBodyName,
  normalizeBodiesRecord,
  type BodyTreeNode,
} from "./bodyActionUtils";

export const createBodySetActions = (set: SceneStoreSetState): Pick<BodyActions, "setBodies"> => ({
  setBodies: (
    bodiesInput: Body[] | Record<string, Body>,
    worldBodiesInput?: WorldBody[] | string[]
  ) => {
    if (shouldBlockPhysicsMutation("setBodies")) return;
    let deletedBodyUuidsForExternalCleanup = new Set<string>();
    set((state) =>
      create(state, (draft) => {
        const previousBodyUuids = new Set<string>(Object.keys(state?.bodies || {}));
        // Handle both array and Record input formats
        let bodies: Record<string, Body>;
        let worldBodies: WorldBody[];

        if (Array.isArray(bodiesInput)) {
          // Array input: flatten into Record structure
          const flattened = flattenBodyTree(bodiesInput as unknown as BodyTreeNode[]);
          bodies = flattened.bodies;
          worldBodies = flattened.worldBodies;
        } else {
          // Record input: normalize so keys are UUIDs and every body has a uuid
          const normalized = normalizeBodiesRecord(bodiesInput);
          bodies = normalized.bodies;

          // Convert worldBodiesInput to WorldBody[] format
          if (!worldBodiesInput || worldBodiesInput.length === 0) {
            worldBodies = [];
          } else if (typeof worldBodiesInput[0] === "string") {
            // Legacy format: string[] - convert to WorldBody[]
            const existingWorldBodies = (draft.worldBodies || []) as WorldBody[];
            const convertedWorldBodies: WorldBody[] = [];
            (worldBodiesInput as string[]).forEach((uuid) => {
              const mapped = normalized.keyToUuid[String(uuid)] || (uuid as BodyUUID);
              if (!bodies[mapped]) {
                logger.warn("[setBodies] worldBodies references missing body; dropping", {
                  uuid,
                  mapped,
                });
                return;
              }
              // Always generate "Object 1", "Object 2", etc. for robot-level world body labels
              // Check against existing worldBodies and already converted ones
              const name = generateUniqueWorldBodyName([
                ...existingWorldBodies,
                ...convertedWorldBodies,
              ]);
              convertedWorldBodies.push({ uuid: mapped as BodyUUID, name });
            });
            worldBodies = convertedWorldBodies;
          } else {
            // Already WorldBody[] format
            worldBodies = (worldBodiesInput as WorldBody[])
              .map((wb) => {
                const mapped = normalized.keyToUuid[String(wb?.uuid)] || (wb.uuid as BodyUUID);
                if (!bodies[mapped]) {
                  logger.warn("[setBodies] worldBodies references missing body; dropping", {
                    uuid: wb?.uuid,
                    mapped,
                  });
                  return null;
                }
                return { ...wb, uuid: mapped };
              })
              .filter(Boolean) as WorldBody[];
          }
        }

        draft.bodies = bodies;
        draft.worldBodies = worldBodies;
        const nextBodyUuids = new Set<string>(Object.keys(bodies || {}));
        deletedBodyUuidsForExternalCleanup = new Set(
          Array.from(previousBodyUuids).filter((uuid) => !nextBodyUuids.has(uuid))
        );

        // Material remapping
        const materialLookup: Record<string, string> = {};
        Object.entries(draft.materials as Record<string, Material>).forEach(([id, mat]) => {
          materialLookup[id] = id;
          if (mat?.name) {
            materialLookup[mat.name] = id;
          }
        });
        if (Object.keys(materialLookup).length > 0) {
          const missingMaterialKeys = new Set<string>();
          const remapGeomMaterials = (bodyMap: Record<string, Body>) => {
            Object.values(bodyMap).forEach((node) => {
              if (!node) return;
              if (Array.isArray(node.geoms)) {
                node.geoms.forEach((geom) => {
                  if (!geom || !Array.isArray(geom.materials)) return;
                  geom.materials = geom.materials.map((key: string) => {
                    const remapped = materialLookup[key];
                    if (!remapped) {
                      missingMaterialKeys.add(key);
                      return key as MaterialUUID;
                    }
                    return remapped as MaterialUUID;
                  });
                });
              }
            });
          };
          remapGeomMaterials(draft.bodies);
          if (missingMaterialKeys.size > 0) {
            logger.warn("[setBodies] Some geom materials were not found in material lookup", {
              materialKeys: Array.from(missingMaterialKeys),
              available: Object.keys(materialLookup),
            });
          }
        }
      })
    );
    if (deletedBodyUuidsForExternalCleanup.size > 0) {
      cleanupReferencesForDeletedTargets({
        deletedBodyUuids: deletedBodyUuidsForExternalCleanup,
        external: { grasps: true },
      });
    }
  },
});
